/**
 * Top-level review-mode overlay mounted on top of the public video viewer when ?review=true.
 *
 * Owns:
 *   - identity gating (forces the modal until the reviewer enters a name)
 *   - the floating "+ Add comment" pill
 *   - the comment composer (auto-pauses player on open)
 *   - the right-side drawer with the reviewer's draft list
 *   - the header chip showing reviewer identity + queue count + send button
 *
 * Communicates with the player via the playerRef passed in from the viewer route — the
 * in-page VideoCompositionPlayer, or the embedded-player adapter over the baked iframe.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquarePlus, ListChecks, UserCircle2, CheckCircle2 } from 'lucide-react';
import type { VideoCompositionPlayerRef } from '../../video-workspace/VideoCompositionPlayer';
import type { VideoManifest, VideoPlaybackState } from '../../../types/videoManifest';
import { useReviewSession } from './useReviewSession';
import { ReviewerIdentityModal } from './ReviewerIdentityModal';
import { CommentComposer } from './CommentComposer';
import { MyReviewDrawer } from './MyReviewDrawer';
import { initials } from './format';

interface Props {
  shareToken: string;
  manifest: VideoManifest | null;
  playerRef: React.RefObject<Pick<VideoCompositionPlayerRef, 'getState' | 'pause' | 'seek'> | null>;
  playbackState: VideoPlaybackState;
  videoTitle: string | null;
}

interface Anchor {
  slideId: string;
  slideIndex: number;
  slideTitle: string;
  timestamp: number;
}

export function ReviewModeOverlay({ shareToken, manifest, playerRef, playbackState, videoTitle }: Props) {
  const init = useReviewSession((s) => s.init);
  const identity = useReviewSession((s) => s.identity);
  const drafts = useReviewSession((s) => s.drafts);
  const lastSubmittedAt = useReviewSession((s) => s.lastSubmittedAt);

  const [identityOpen, setIdentityOpen] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  /** Count of comments in the most recent successful submission, shown in a transient banner. */
  const [submittedCount, setSubmittedCount] = useState(0);
  const wasPlayingOnOpenRef = useRef(false);

  // Hydrate session + force identity capture on first mount
  useEffect(() => {
    init(shareToken);
  }, [init, shareToken]);

  useEffect(() => {
    if (!identity) {
      setIdentityOpen(true);
    }
  }, [identity]);

  // Auto-dismiss the success banner a few seconds after submission
  useEffect(() => {
    if (!lastSubmittedAt || submittedCount === 0) return;
    const id = window.setTimeout(() => setSubmittedCount(0), 4000);
    return () => window.clearTimeout(id);
  }, [lastSubmittedAt, submittedCount]);

  const captureAnchor = useCallback((): Anchor | null => {
    const state = playerRef.current?.getState();
    if (!state) return null;
    const slide = manifest?.slides[state.currentSlideIndex];
    const slideId = state.currentSlideId || slide?.id;
    if (!slideId) return null;
    return {
      slideId,
      slideIndex: state.currentSlideIndex,
      slideTitle: slide?.title ?? '',
      timestamp: state.currentTime,
    };
  }, [manifest, playerRef]);

  const openComposer = useCallback(() => {
    if (!identity) {
      setIdentityOpen(true);
      return;
    }
    const a = captureAnchor();
    if (!a) return;
    wasPlayingOnOpenRef.current = playbackState.isPlaying;
    if (playbackState.isPlaying) playerRef.current?.pause();
    setAnchor(a);
    setComposerOpen(true);
  }, [identity, captureAnchor, playbackState.isPlaying, playerRef]);

  const closeComposer = useCallback(() => {
    setComposerOpen(false);
    // Don't auto-resume — the reviewer probably wants to continue inspecting.
  }, []);

  // 'c' shortcut to open composer (skip when typing or already inside the composer)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      }
      if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        openComposer();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openComposer]);

  const handleSeek = useCallback(
    (seconds: number, _slideId: string) => {
      playerRef.current?.seek(seconds, { pauseAfterSeek: true });
      setDrawerOpen(false);
    },
    [playerRef],
  );

  const draftCount = drafts.length;

  const reviewerLabel = useMemo(() => identity?.name ?? 'Reviewer', [identity]);

  return (
    <>
      {/* Top-right header chip — sits over the existing player header */}
      <div className="fixed top-3 right-4 z-30 flex items-center gap-2 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="pointer-events-auto flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-amber-500/90 text-amber-950 text-xs font-semibold tracking-wide shadow"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-amber-950 animate-pulse" />
          REVIEW MODE
        </motion.div>
        {identity && (
          <motion.button
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => setIdentityOpen(true)}
            className="pointer-events-auto flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-900/80 backdrop-blur border border-white/10 text-white/90 text-xs hover:bg-zinc-800"
            title="Change reviewer identity"
          >
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/15 text-[10px] font-bold">
              {initials(identity.name)}
            </span>
            <span className="hidden sm:inline">{reviewerLabel}</span>
          </motion.button>
        )}
        {identity && draftCount > 0 && (
          <motion.button
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => setDrawerOpen(true)}
            className="pointer-events-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/95 text-emerald-950 text-xs font-semibold shadow hover:bg-emerald-400"
          >
            <ListChecks className="h-3.5 w-3.5" />
            {draftCount} pending
          </motion.button>
        )}
      </div>

      {/* Floating "+ Add comment" pill — bottom right, above player controls */}
      <AnimatePresence>
        {identity && !composerOpen && (
          <motion.button
            key="add-pill"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.18 }}
            onClick={openComposer}
            className="fixed bottom-24 right-6 z-30 inline-flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-white text-zinc-900 shadow-2xl hover:bg-zinc-100 font-medium text-sm"
            title="Add comment (c)"
          >
            <MessageSquarePlus className="h-4 w-4" />
            Add comment
            <kbd className="ml-1 px-1.5 py-0.5 text-[10px] font-mono bg-zinc-200 rounded">c</kbd>
          </motion.button>
        )}
      </AnimatePresence>

      <CommentComposer open={composerOpen} anchor={anchor} onClose={closeComposer} />

      <MyReviewDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSeek={handleSeek}
        onSubmitted={(count) => {
          setDrawerOpen(false);
          setSubmittedCount(count);
        }}
      />

      {/* Inline success banner — shown briefly after a successful submission */}
      <AnimatePresence>
        {submittedCount > 0 && (
          <motion.div
            key="sent-banner"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="fixed top-16 left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-emerald-950 text-sm font-medium shadow-lg"
          >
            <CheckCircle2 className="h-4 w-4" />
            {submittedCount} comment{submittedCount === 1 ? '' : 's'} sent to the creator
          </motion.div>
        )}
      </AnimatePresence>

      <ReviewerIdentityModal
        open={identityOpen}
        onOpenChange={setIdentityOpen}
        videoTitle={videoTitle}
        required={!identity}
      />

      {/* Subtle hint when there are no drafts and no identity prompt is up */}
      {identity && draftCount === 0 && !composerOpen && !drawerOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-20 text-xs text-white/50 pointer-events-none flex items-center gap-1.5"
        >
          <UserCircle2 className="h-3.5 w-3.5" />
          Reviewing as {identity.name} — pause anywhere and press <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-[10px] font-mono">c</kbd> to comment
        </motion.div>
      )}
    </>
  );
}
